import { useEffect, useState } from 'react';
import type { ApiClient } from '../api/client';

/**
 * Libellés des objets qu'une page de liste référence (ADR-094) : une lecture par type cité,
 * `filter[id][in]=…`, sous l'autorisation de l'utilisateur. Un objet qu'il ne peut pas lire reste
 * sans libellé : la cellule montre l'identifiant abrégé, jamais une donnée devinée. Une erreur de
 * lecture n'empêche pas la liste de s'afficher.
 */

export type ReferenceLabels = ReadonlyMap<string, string>;

/** Libellés et images des objets cités (ADR-103) : l'image est l'identifiant du fichier que l'objet déclare (`meta.image_field`). */
export interface ReferenceIndex {
  labels: ReferenceLabels;
  images: ReadonlyMap<string, string>;
}

/** Champs dont la valeur sert de libellé à un objet, dans l'ordre de préférence. */
export const LABEL_KEYS = ['display_name', 'name', 'label', 'title', 'reference', 'code', 'lot_code'] as const;

export function labelOfRecord(record: Readonly<Record<string, unknown>>): string | undefined {
  for (const key of LABEL_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') return value;
    if (value !== null && typeof value === 'object' && typeof (value as Record<string, unknown>)['fr'] === 'string') return (value as Record<string, string>)['fr'];
  }
  return undefined;
}

/** Identifiant abrégé, en dernier recours. */
export function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-6)}` : id;
}

const MAX_IDS_PER_TYPE = 200;

export function useReferenceLabels(client: ApiClient, wanted: ReadonlyArray<{ type: string; ids: readonly string[] }>): ReferenceLabels {
  return useReferenceIndex(client, wanted).labels;
}

export function useReferenceIndex(client: ApiClient, wanted: ReadonlyArray<{ type: string; ids: readonly string[] }>): ReferenceIndex {
  const [index, setIndex] = useState<ReferenceIndex>({ labels: new Map(), images: new Map() });
  const key = JSON.stringify(wanted.map((entry) => [entry.type, [...new Set(entry.ids)].sort()]));
  useEffect(() => {
    let alive = true;
    const requests = wanted
      .map((entry) => ({ type: entry.type, ids: [...new Set(entry.ids)].filter((id) => /^[0-9a-f]{24}$/.test(id)).slice(0, MAX_IDS_PER_TYPE) }))
      .filter((entry) => entry.ids.length > 0);
    if (requests.length === 0) return;
    void Promise.allSettled(requests.map((entry) => client.listObjects<Record<string, unknown>>(entry.type, { filter: { id: { in: entry.ids.join(',') } }, limit: entry.ids.length }))).then((results) => {
      if (!alive) return;
      const labels = new Map<string, string>();
      const images = new Map<string, string>();
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const imageField = typeof result.value.meta['image_field'] === 'string' ? result.value.meta['image_field'] : undefined;
        for (const row of result.value.data) {
          if (typeof row['id'] !== 'string') continue;
          const label = labelOfRecord(row);
          if (label !== undefined) labels.set(row['id'], label);
          const image = imageField === undefined ? undefined : row[imageField];
          if (typeof image === 'string' && /^[0-9a-f]{24}$/.test(image)) images.set(row['id'], image);
        }
      }
      setIndex({ labels, images });
    });
    return () => {
      alive = false;
    };
    // La clé résume `wanted` : une nouvelle page ou un autre jeu d'identifiants relance la lecture.
  }, [client, key]);
  return index;
}
